// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.
import type { AppData, Parliamentarian } from './types';
import { loadMpDetail } from './data';
import { partyStyle, chamberColour } from './parties';
import { fmtDollars, fmtMoney, fmtQuarter, escapeHtml, stateName } from './format';

let median = 0;
let rankMap: Map<string, number> = new Map();

export function initDrawer(data: AppData, onClose: () => void): void {
  // Precompute median + overall rank once.
  const totals = data.people.map((p) => p.total).sort((a, b) => a - b);
  const mid = Math.floor(totals.length / 2);
  median = totals.length % 2 ? totals[mid] : (totals[mid - 1] + totals[mid]) / 2;
  const sorted = [...data.people].sort((a, b) => b.total - a.total);
  rankMap = new Map(sorted.map((p, i) => [p.slug, i + 1]));

  const scrim = document.getElementById('drawer-scrim')!;
  const drawer = document.getElementById('mp-drawer')!;
  scrim.addEventListener('click', onClose);
  drawer.addEventListener('click', (e) => {
    if ((e.target as Element).closest('.close')) onClose();
  });
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && drawer.classList.contains('open')) onClose();
  });
}

export function closeDrawer(): void {
  document.getElementById('drawer-scrim')!.classList.remove('open');
  document.getElementById('mp-drawer')!.classList.remove('open');
}

function catBars(p: Parliamentarian): string {
  const entries = Object.entries(p.byCategory)
    .filter(([, v]) => v !== 0)
    .sort((a, b) => b[1] - a[1]);
  const max = Math.max(...entries.map(([, v]) => Math.abs(v)), 1);
  const col = partyStyle(p.partyCode).colour;
  return entries
    .map(([name, v]) => {
      const w = (Math.abs(v) / max) * 100;
      const tip = `${escapeHtml(name)}: ${fmtDollars(v)}`;
      return `<div class="cat-line" data-tip="${tip}">
        <div class="cl-label"><span class="name">${escapeHtml(name)}</span></div>
        <div class="cl-val">${fmtDollars(v)}</div>
        <div class="cl-bar"><i style="width:${w.toFixed(1)}%;background:${col}"></i></div>
      </div>`;
    })
    .join('');
}

function quarterlyBars(p: Parliamentarian): string {
  const vals = p.quarterly;
  const max = Math.max(...vals.map((q) => q.amount), 1);
  const col = partyStyle(p.partyCode).colour;
  const bars = vals
    .map((q) => {
      const h = Math.max(2, (q.amount / max) * 100);
      const tip = `${fmtQuarter(q.q)}: ${fmtDollars(q.amount)}`;
      return `<div style="flex:1;display:flex;flex-direction:column;justify-content:flex-end;align-items:center;gap:3px;min-width:0" data-tip="${tip}">
        <div style="width:100%;max-width:26px;height:${h}%;background:${col};border-radius:2px 2px 0 0"></div>
      </div>`;
    })
    .join('');
  const first = vals[0]?.q ? fmtQuarter(vals[0].q) : '';
  const last = vals[vals.length - 1]?.q ? fmtQuarter(vals[vals.length - 1].q) : '';
  return `<div style="height:110px;display:flex;align-items:flex-end;gap:3px">${bars}</div>
    <div style="display:flex;justify-content:space-between;font-size:0.6875rem;color:var(--text-tertiary);margin-top:4px"><span>${first}</span><span>${last}</span></div>`;
}

export async function openDrawer(slug: string, data: AppData): Promise<void> {
  const p = data.bySlug.get(slug);
  const scrim = document.getElementById('drawer-scrim')!;
  const drawer = document.getElementById('mp-drawer')!;
  if (!p) {
    return;
  }
  const ps = partyStyle(p.partyCode);
  const rank = rankMap.get(p.slug) ?? 0;
  const vsMedian = median ? p.total / median : 0;
  const seat = p.chamber === 'Senate' ? `Senator for ${escapeHtml(stateName(p.state ?? ''))}` : p.electorate ? `Member for ${escapeHtml(titleCaseWord(p.electorate))}${p.state ? ', ' + escapeHtml(p.state) : ''}` : escapeHtml(p.role ?? '');
  const activeQ = p.quarterly.filter((q) => q.amount !== 0).length;

  drawer.innerHTML = `
    <div class="drawer-head">
      <div style="min-width:0">
        <h2>${escapeHtml(p.name)}</h2>
        <div class="meta">
          <span class="pill" style="background:${ps.colour}"><span class="dot"></span>${escapeHtml(ps.name)}</span>
          <span class="chamber-tag" style="background:${chamberColour(p.chamber)}">${escapeHtml(p.chamber)}</span>
          <span>${seat}</span>${p.isCurrent ? '' : ' <span style="opacity:0.8">· former</span>'}
        </div>
      </div>
      <button class="close" aria-label="Close panel">×</button>
    </div>
    <div class="drawer-body">
      <div class="drawer-stat-row">
        <div class="drawer-stat"><div class="label">Total expenses</div><div class="value">${fmtDollars(p.total)}</div></div>
        <div class="drawer-stat"><div class="label">Overall rank</div><div class="value">#${rank}<span style="font-size:0.8rem;color:var(--text-tertiary)"> / ${data.people.length}</span></div></div>
        <div class="drawer-stat"><div class="label">vs median</div><div class="value">${vsMedian.toFixed(1)}×</div></div>
        <div class="drawer-stat"><div class="label">Quarters active</div><div class="value">${activeQ}</div></div>
      </div>

      <div class="drawer-section">
        <h3>Where the money went</h3>
        ${catBars(p)}
      </div>

      <div class="drawer-section">
        <h3>Spending by quarter</h3>
        ${quarterlyBars(p)}
      </div>

      <div class="drawer-section">
        <h3>Biggest single claims</h3>
        <div id="drawer-claims"><div class="state-msg"><div class="spinner"></div>Loading line items…</div></div>
      </div>

      <p style="font-size:0.75rem;color:var(--text-tertiary)">Figures are net of adjustments and repayments. The median parliamentarian in this window spent ${fmtMoney(median)}.</p>
    </div>`;

  scrim.classList.add('open');
  drawer.classList.add('open');
  drawer.scrollTop = 0;

  // Lazy-load the biggest line items.
  const claimsEl = document.getElementById('drawer-claims');
  try {
    const detail = await loadMpDetail(slug);
    if (!claimsEl) return;
    if (!detail.topClaims.length) {
      claimsEl.innerHTML = '<p style="color:var(--text-tertiary);font-size:0.8rem">No itemised claims recorded.</p>';
      return;
    }
    claimsEl.innerHTML = detail.topClaims
      .slice(0, 12)
      .map((c) => {
        const desc = c.description && c.description.toLowerCase() !== 'aggregated total' ? c.description : `${c.category} — quarterly total`;
        const sub = [c.major || c.category, fmtQuarter(c.period)].filter(Boolean).join(' · ');
        return `<div class="claim-item">
          <div class="desc"><div class="d1">${escapeHtml(desc)}</div><div class="d2">${escapeHtml(sub)}</div></div>
          <div class="amt">${fmtDollars(c.amount)}</div>
        </div>`;
      })
      .join('');
  } catch {
    if (claimsEl) claimsEl.innerHTML = '<p style="color:var(--status-bad);font-size:0.8rem">Could not load line items.</p>';
  }
}

function titleCaseWord(s: string): string {
  return s
    .toLowerCase()
    .replace(/\b([a-z])/g, (m) => m.toUpperCase())
    .replace(/\bMp\b/, 'MP');
}
